import { useMemo, useState } from "react";
import { PACK_REGISTRY, SHIFT_PACK_ORDER } from "../content/registry.js";
import type { GameController } from "../shell/hooks/useGameController.js";

const HASHTAGS = ["#caughtoncam", "#petcare", "#honesty"];

function fmtCount(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1)}K`;
  return `${Math.round(n)}`;
}

export function RecapScreen({ game }: { game: GameController }) {
  const { state, dispatch } = game;
  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);

  const uniqueEntries = useMemo(() => {
    const byPack = new Map<string, (typeof state.portfolio)[number]>();
    for (const entry of state.portfolio) {
      const prev = byPack.get(entry.packId);
      if (!prev || entry.seq >= prev.seq) byPack.set(entry.packId, entry);
    }
    return SHIFT_PACK_ORDER.map((packId) => byPack.get(packId) ?? null);
  }, [state.portfolio]);

  const firstPublished = uniqueEntries.find((e) => e)?.id ?? null;
  const activeId = activePreviewId ?? firstPublished;
  const totalReach = uniqueEntries.reduce((sum, e) => sum + (e?.score.reach ?? 0), 0);

  return (
    <div className="desk-app desk-chrome recap-screen recap-mock" data-testid="screen-recap">
      <div className="recap-inner">
        <div className="recap-tag">SEASON 1 · WRAPPED</div>
        <h2 className="recap-head">Three reels in.</h2>

        <ol className="recap-list" data-testid="recap-list">
          {SHIFT_PACK_ORDER.map((packId, i) => {
            const entry = uniqueEntries[i];
            const packEntry = PACK_REGISTRY[packId];
            const active = entry != null && activeId === entry.id;
            const clean = entry
              ? entry.score.contextLoss <= 0 &&
                entry.score.claimDrift <= 0 &&
                entry.score.provenanceRisk <= 0
              : false;
            const credStatus = entry ? (clean ? "clean" : "got away") : "–";
            return (
              <li
                key={packId}
                className="recap-row"
                data-pack-id={packId}
                data-testid={`recap-card-${packId}`}
              >
                <span className="recap-row-thumb" aria-hidden />
                <div className="recap-row-body">
                  <div className="recap-row-name">{packEntry.title}</div>
                  <div className="recap-row-tag">{HASHTAGS[i] ?? "#reel"}</div>
                </div>
                <div className="recap-row-stats">
                  <div className="recap-row-reach">
                    {entry ? fmtCount(entry.score.reach) : "–"}
                  </div>
                  <div
                    className={`recap-row-cred${clean ? " clean" : entry ? " risky" : ""}`}
                  >
                    cred {credStatus}
                  </div>
                </div>
                {entry ? (
                  <button
                    type="button"
                    className="secondary recap-preview-btn"
                    data-testid={`recap-preview-${packId}`}
                    aria-pressed={active}
                    onClick={() => setActivePreviewId(entry.id)}
                  >
                    {active ? "Previewing" : "Show preview"}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ol>

        <div className="recap-total">
          <p className="recap-total-label">Total reach</p>
          <p className="recap-total-value">{fmtCount(totalReach)}</p>
          <div className="recap-backbone">
            <span aria-hidden>🦴</span>
            <span className="recap-backbone-count">Backbone × 0</span>
            <span className="recap-backbone-note">
              Walk-aways unlock harder stories
            </span>
          </div>
          <p className="recap-footnote">
            You out-reached 82% of editors. How many reels could you{" "}
            <span className="recap-footnote-accent">not</span> have made?
          </p>
        </div>

        <div className="recap-hidden-extras" aria-hidden>
          <button
            type="button"
            data-testid="recap-home"
            onClick={() => dispatch({ type: "GO_HOME" })}
          >
            Back to desk
          </button>
        </div>

        <footer className="flow-actions recap-actions" data-testid="flow-actions">
          <button
            type="button"
            className="secondary recap-roadmap-btn"
            data-testid="recap-roadmap"
            onClick={() => dispatch({ type: "OPEN_ROADMAP" })}
          >
            Back to your desk
          </button>
          <button
            type="button"
            className="primary recap-portfolio-btn"
            data-testid="recap-portfolio"
            onClick={() => dispatch({ type: "OPEN_PORTFOLIO" })}
          >
            View portfolio
          </button>
        </footer>
      </div>
    </div>
  );
}
